import React from 'react';
import { Card, Typography, Empty, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import { LinkOutlined } from '@ant-design/icons';
import { Job } from '../types/job.types';
import { formatDate } from '../../../../../utils/date.util';
import RenderDetails, { DetailItem } from '../../../../common/RenderDetails';

const { Text } = Typography;

interface JobDetailsComponentProps {
  data: Job | undefined;
  success: boolean;
  isLoading: boolean;
  error: Error | null;
}

const JobDetailsComponent: React.FC<JobDetailsComponentProps> = ({
  data,
  success,
  isLoading,
  error,
}) => {
  const { t } = useTranslation();
  const hasDownloadLink = !!data?.link;

  // Create details array
  const details: DetailItem[] = [
    {
      label: 'jobs.jobName.label',
      value: data?.jobName || '',
    },
    {
      label: 'jobs.createdBy.label',
      value: data?.createdBy ? (
        <div>
          <div>{data.createdBy.name}</div>
          <Text type="secondary" className="small">
            {data.createdBy.email}
          </Text>
        </div>
      ) : (
        '-'
      ),
    },
    ...(data?.triggerStatus
      ? [
          {
            label: 'jobs.triggerStatus.label',
            value: (data.triggerStatus as any).statusCode || data.triggerStatus.code || '-',
          },
        ]
      : []),
    ...(data?.successStatus
      ? [
          {
            label: 'jobs.successStatus.label',
            value: (data.successStatus as any).statusCode || data.successStatus.code || '-',
          },
        ]
      : []),
    ...(data?.failedBotStatus
      ? [
          {
            label: 'jobs.failedBotStatus.label',
            value: (data.failedBotStatus as any).statusCode || data.failedBotStatus.code || '-',
          },
        ]
      : []),
    ...(data?.failedDataStatus
      ? [
          {
            label: 'jobs.failedDataStatus.label',
            value: (data.failedDataStatus as any).statusCode || data.failedDataStatus.code || '-',
          },
        ]
      : []),
    ...(hasDownloadLink
      ? []
      : [
          {
            label: 'jobs.total.label',
            value: data?.total?.toString() || '0',
          },
          {
            label: 'jobs.success.label',
            value: data?.success?.toString() || '0',
          },
          {
            label: 'jobs.failed.label',
            value: data?.failed?.toString() || '0',
          },
        ]),
    ...(data?.email
      ? [
          {
            label: 'jobs.email.label',
            value: data.email,
          },
        ]
      : []),
    ...(data?.link
      ? [
          {
            label: 'jobs.link.label',
            value: (
              <a
                href={data.link}
                target="_blank"
                rel="noopener noreferrer"
                className="d-flex align-items-center gap-1"
              >
                <LinkOutlined />
                <span>{t('common.download')}</span>
              </a>
            ),
          },
        ]
      : []),
    ...(data?.createdAt
      ? [
          {
            label: 'common.createdAt.label',
            value: formatDate(data.createdAt.toString()),
          },
        ]
      : []),
    ...(data?.updatedAt
      ? [
          {
            label: 'common.updatedAt.label',
            value: formatDate(data.updatedAt.toString()),
          },
        ]
      : []),
  ];

  if (isLoading) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 6 }} />
      </Card>
    );
  }

  if (error || !success) {
    return (
      <Card>
        <Empty description={t('common.errorLoadingData')} />
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <Empty description={t('common.noDataAvailable')} />
      </Card>
    );
  }

  return (
    <div className="row">
      <div className="col-12">
        <RenderDetails data={details} columns={2} isLoading={isLoading} />
      </div>
    </div>
  );
};

export default JobDetailsComponent;
